import { measure } from '@/components/Analytics/Analytics';
import Button from '@/components/Button/Button';
import ArrowRightIcon from '@/components/Icon/ArrowRightIcon';
import HeartIcon from '@/components/Icon/HeartIcon';
import StarIcon from '@/components/Icon/StarIcon';
import LargeSquareBackground from '@/components/LargeSquareBackground/LargeSquareBackground';
import useIsNative, { useNativePlatform } from '@/hooks/native/useIsNative';
import { getFeedbackUserUUID, submitFeedback } from '@/lib/feedback';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { motion, useReducedMotion } from 'framer-motion';
import {
	Dispatch,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react';
import JanoukFeedback from './janouk_feedback.png';

const Feedback = () => {
	const reduceMotion = useReducedMotion();
	const isNative = useIsNative();
	const nativePlatform = useNativePlatform();

	const [stars, setStars] = useState<number | null>(null);
	const [isTextInputShown, setIsTextInputShown] = useState(false);
	const [sentFeedback, setSentFeedback] = useState<{
		stars: number;
	} | null>(null);

	const isFeedbackSent = useMemo(() => sentFeedback !== null, [sentFeedback]);
	const is5StarsSent = useMemo(
		() => sentFeedback?.stars === 5,
		[sentFeedback],
	);

	if (!sentFeedback && nativePlatform === 'ios') return null;

	return (
		<div className="px-2 py-6 xs:px-6">
			<section
				aria-label="Feedback"
				className="relative mx-auto w-full max-w-lg">
				<LargeSquareBackground className="text-primary" />
				<div className="relative flex text-white xxs:ps-2">
					<div className="shrink-0">
						<img
							src={JanoukFeedback}
							alt="Janouk kijkt naar jou"
							className="hidden w-24 -translate-y-6 object-contain object-top xxs:block sm:w-32"
						/>
					</div>
					<form
						className="py-6 pe-4 ps-8 xxs:ps-4"
						onSubmit={(event) => event.preventDefault()}>
						<h2 className="font-brand text-4xl first-letter:text-5xl">
							{isFeedbackSent ? 'Dankjewel!' : 'Wat vind jij?'}
						</h2>
						<p className="pb-2 md:text-lg">
							{isFeedbackSent
								? 'Nu kunnen we SoortSafari nog leuker maken.'
								: 'Hoeveel sterren geef jij SoortSafari vandaag?'}
							{isFeedbackSent && (
								<>
									{/* Prompt the user to leave a store review */}
									{isNative && is5StarsSent && (
										<span className="flex items-center gap-2 py-2 md:text-lg">
											Wil je ook een review achterlaten in
											de{' '}
											{nativePlatform === 'ios'
												? 'App Store'
												: 'Play Store'}
											?
											<img
												src={
													nativePlatform === 'ios'
														? '/assets/logos/app-store.jpg'
														: '/assets/logos/play-store.jpg'
												}
												alt="App winkel logo"
												className="size-12 -translate-x-2 rotate-6 rounded-lg"
											/>
										</span>
									)}
									{/* Heart animation */}
									<motion.div
										className="w-fit ps-2 pt-2"
										initial={{
											scale: 0,
										}}
										animate={{
											scale: [0, 2, 1.5],
											rotate: -6,
											transition: {
												duration: 1,
												bounce: 1,
											},
										}}>
										<HeartIcon />
									</motion.div>
								</>
							)}
						</p>
						{!isFeedbackSent && (
							<>
								<StarsInput stars={stars} setStars={setStars} />
								<motion.div
									className="w-fit"
									animate={{
										scale:
											reduceMotion || !stars
												? 1
												: [1, 1.1],
										transition: {
											repeat: stars ? Infinity : 0,
											repeatType: 'reverse',
											duration: 1.5,
										},
									}}>
									<Button
										type="submit"
										className="mt-4"
										textClassName="text-accentCompat"
										backgroundClassName="text-accent"
										label="Verstuur"
										icon={<ArrowRightIcon />}
										disabled={stars === null}
										onClick={() => {
											measure(
												`feedback-general-stars-${stars!}-${getFeedbackUserUUID()}`,
											);
											setIsTextInputShown(true);
										}}
									/>
								</motion.div>
							</>
						)}
					</form>
				</div>
				{isTextInputShown && stars !== null && !isFeedbackSent && (
					<TextInput
						stars={stars}
						setSentFeedback={setSentFeedback}
					/>
				)}
			</section>
		</div>
	);
};

const StarsInput = ({
	stars,
	setStars,
}: {
	stars: number | null;
	setStars: (stars: number) => void;
}) => {
	const reduceMotion = useReducedMotion();

	return (
		<fieldset>
			<legend className="sr-only">Beoordeling</legend>
			<div className="flex w-fit gap-1 rounded-full sm:gap-2 md:gap-3 lg:gap-4">
				{[1, 2, 3, 4, 5].map((star) => (
					<motion.div
						key={star}
						className="relative"
						animate={{
							scale: stars !== null && star <= stars ? 1.25 : 1,
							transition: {
								stiffness: 10,
								bounce: 3,
							},
						}}
						whileTap={{
							scale: reduceMotion ? 1 : 2,
						}}
						onClick={() => setStars(star)}
						tabIndex={-1}>
						<input
							name="stars"
							type="radio"
							aria-label={
								stars === 1 ? '1 ster' : `${stars ?? 0} sterren`
							}
							className="absolute inset-0 block size-full cursor-pointer rounded-full p-1 accent-yellow-300 opacity-0 focus-visible:opacity-100"
							onChange={() => setStars(star)}
						/>
						<StarIcon
							className={`h-7 transition-colors md:h-10 ${
								stars === null ? 'text-white' : ''
							} ${
								stars !== null && star <= stars
									? 'text-yellow-300'
									: 'text-white'
							}`}
						/>
						{stars !== null && star > stars && (
							<StarIcon className="absolute start-1/2 top-1/2 h-7 -translate-x-1/2 -translate-y-1/2 scale-50 animate-fade-in text-primary md:h-10" />
						)}
					</motion.div>
				))}
			</div>
		</fieldset>
	);
};

const TextInput = ({
	setSentFeedback,
	...props
}: {
	stars: number;
	setSentFeedback: Dispatch<{
		stars: number;
	} | null>;
}) => {
	const textInputRef = useRef<HTMLTextAreaElement>(null);
	const textLabelId = useId();

	const [stars, setStars] = useState<number>(props.stars);
	const [text, setText] = useState('');

	const sendFeedback = useCallback(
		(text: string) => {
			submitFeedback('general', stars, text);
			setSentFeedback({
				stars,
			});
		},
		[stars, setSentFeedback],
	);

	useEffect(() => {
		if (textInputRef.current) {
			textInputRef.current.focus();
		}
	}, [textInputRef]);

	return (
		<Dialog open onClose={() => sendFeedback(text)}>
			<DialogPanel className="fixed inset-0 size-full">
				<motion.div
					initial={{ opacity: 0, y: '-10%' }}
					animate={{ opacity: 1, y: 0 }}
					className="flex size-full flex-col overflow-y-auto bg-primary p-4 text-white">
					<DialogTitle
						id={textLabelId}
						className="font-brand text-2xl first-letter:text-3xl">
						Waarom geef je vandaag{' '}
						{stars === 1 ? '1 ster' : `${stars} sterren`}?
					</DialogTitle>
					<form
						onSubmit={(event) => event.preventDefault()}
						className="flex flex-col gap-4">
						<StarsInput stars={stars} setStars={setStars} />
						<textarea
							ref={textInputRef}
							autoFocus
							className="h-24 w-full resize-y text-balance rounded-md border-b-2 bg-white p-2 text-lg text-slate-900 placeholder:text-slate-700"
							placeholder="Vul hier in wat je vandaag van SoortSafari vindt"
							value={text}
							onChange={(event) => setText(event.target.value)}
							aria-labelledby={textLabelId}
						/>
						<Button
							type="submit"
							label={text.length > 0 ? 'Verstuur' : 'Overslaan'}
							icon={<ArrowRightIcon />}
							className="me-auto"
							textClassName="text-accentCompat"
							backgroundClassName="text-accent"
							onClick={() => sendFeedback(text)}
						/>
					</form>
				</motion.div>
			</DialogPanel>
		</Dialog>
	);
};

export default Feedback;
